import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import PersonIcon from "@mui/icons-material/Person";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import baseUrl from "../../../../api/baseUrl";
import { patienttitles } from "../../../../utils/title";
import { UI } from "../components/appointmentScheduleUi";
import {
  resolvePatientTypeLabelFromPatientAndTreatments,
} from "../../viewappointment/components/appointmentViewShared";
import PatientTypeBadge from "../../viewappointment/components/PatientTypeBadge";
import type { NewAppointmentFormState } from "./newAppointmentForm";

const SEARCH_DEBOUNCE_MS = 280;
const PHONE_CHECK_DEBOUNCE_MS = 400;

export type PatientStepMode = "entry" | "existing" | "new";

type AppointmentPatientStepProps = {
  formData: NewAppointmentFormState;
  setformData: React.Dispatch<React.SetStateAction<NewAppointmentFormState>>;
  error: boolean;
  setError: (v: boolean) => void;
  onViewPatient?: (e: React.MouseEvent) => void;
  purposeRef?: React.RefObject<HTMLElement>;
  patientMode: PatientStepMode;
  setPatientMode: React.Dispatch<React.SetStateAction<PatientStepMode>>;
};

function phoneDigits(value: unknown) {
  return String(value ?? "").replace(/\D/g, "");
}

function PatientTypeBadges({
  patientTypeLabel,
}: {
  patientTypeLabel?: string | null;
}) {
  return <PatientTypeBadge label={patientTypeLabel} />;
}

function mergePatientsById(lists: any[][]) {
  const map = new Map<string, any>();
  for (const list of lists) {
    for (const p of list || []) {
      const id = String(p?._id || "");
      if (!id || map.has(id)) continue;
      map.set(id, p);
    }
  }
  return Array.from(map.values());
}

export default function AppointmentPatientStep({
  formData,
  setformData,
  error,
  setError,
  onViewPatient,
  purposeRef,
  patientMode,
  setPatientMode,
}: AppointmentPatientStepProps) {
  const [matches, setMatches] = useState<any[]>([]);
  const [searchSettled, setSearchSettled] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState<
    null | "checking" | "exists" | "not_exists"
  >(null);
  const [phonePatients, setPhonePatients] = useState<any[]>([]);

  const fullNameRef = useRef<HTMLInputElement>(null);
  const phoneCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchGen = useRef(0);

  const nameTrim = String(formData.fullName || "").trim();
  const mobile = formData.mobileNo;
  const digits = phoneDigits(mobile);
  const queryReady = nameTrim.length >= 3 || digits.length >= 5;
  const searchReady = nameTrim.length >= 2 || digits.length >= 5;

  const focusPurpose = useCallback(() => {
    window.setTimeout(() => {
      const el = purposeRef?.current;
      if (!el) return;
      if (typeof (el as HTMLElement).focus === "function") {
        (el as HTMLElement).focus();
      } else {
        el.querySelector?.<HTMLElement>("button, input, [tabindex]")?.focus();
      }
    }, 40);
  }, [purposeRef]);

  const selectExisting = useCallback(
    (p: any) => {
      setPatientMode("existing");
      setformData((prev) => ({
        ...prev,
        patientId: p._id,
        fullName: p.fullName || "",
        gender: p.gender || prev.gender || "",
        mobileNo: p.mobileNo || prev.mobileNo || "",
        address: p.address || "",
        title: p.title || prev.title || "",
        patientTypeLabel: p.patientTypeLabel || "",
        registerdDate: p.registerdDate,
        createdAt: p.createdAt,
        regNo: p.regNo || "",
        dob: p.dob ? String(p.dob).slice(0, 10) : prev.dob || "",
      }));
      setError(false);
      setPhoneStatus(null);
      setPhonePatients([]);
      focusPurpose();
    },
    [focusPurpose, setError, setPatientMode, setformData]
  );

  const clearSelection = useCallback(() => {
    setPatientMode("entry");
    setformData((prev) => ({
      ...prev,
      patientId: "",
      patientTypeLabel: "",
      registerdDate: undefined,
      createdAt: undefined,
      regNo: "",
    }));
    window.setTimeout(() => fullNameRef.current?.focus(), 0);
  }, [setPatientMode, setformData]);

  const startRegisterNew = useCallback(() => {
    setPatientMode("new");
    setformData((prev) => ({
      ...prev,
      patientId: "",
      patientTypeLabel: "",
      registerdDate: undefined,
      createdAt: undefined,
      regNo: "",
    }));
    setError(false);
  }, [setError, setPatientMode, setformData]);

  // Autofocus name on open
  useEffect(() => {
    const t = window.setTimeout(() => fullNameRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, []);

  // Debounced patient search from name and/or phone
  useEffect(() => {
    if (patientMode === "existing") return;

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!searchReady) {
      setMatches([]);
      setSearchSettled(true);
      setSearchLoading(false);
      return;
    }

    setSearchSettled(false);
    setSearchLoading(true);
    const gen = ++searchGen.current;

    searchTimer.current = setTimeout(async () => {
      try {
        const requests: Promise<any>[] = [];
        if (digits.length >= 5) {
          requests.push(
            baseUrl.get("appointment/findPatientsToAppointment", {
              headers: { status: true, keyword: digits },
            })
          );
        }
        if (nameTrim.length >= 2) {
          requests.push(
            baseUrl.get("appointment/findPatientsToAppointment", {
              headers: { status: true, keyword: nameTrim },
            })
          );
        }
        const responses = await Promise.all(requests);
        if (gen !== searchGen.current) return;
        const lists = responses.map((r) => r.data?.data ?? []);
        setMatches(mergePatientsById(lists));
      } catch {
        if (gen === searchGen.current) setMatches([]);
      } finally {
        if (gen === searchGen.current) {
          setSearchLoading(false);
          setSearchSettled(true);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [digits, nameTrim, patientMode, searchReady]);

  // Phone duplicate check when registering new
  useEffect(() => {
    if (patientMode !== "new") {
      setPhoneStatus(null);
      setPhonePatients([]);
      return;
    }
    if (phoneCheckTimer.current) clearTimeout(phoneCheckTimer.current);
    if (digits.length < 5) {
      setPhoneStatus(null);
      setPhonePatients([]);
      return;
    }
    setPhoneStatus("checking");
    phoneCheckTimer.current = setTimeout(() => {
      baseUrl
        .post("client/patient/find-patients-by-phonenumber", {
          phonenumber: mobile,
        })
        .then((res) => {
          const patients = Array.isArray(res.data?.data) ? res.data.data : [];
          setPhonePatients(patients);
          setPhoneStatus(patients.length > 0 ? "exists" : "not_exists");
        })
        .catch(() => {
          setPhonePatients([]);
          setPhoneStatus("not_exists");
        });
    }, PHONE_CHECK_DEBOUNCE_MS);
    return () => {
      if (phoneCheckTimer.current) clearTimeout(phoneCheckTimer.current);
    };
  }, [digits, mobile, patientMode]);

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (patientMode === "existing") return;
    setformData((prev) => ({ ...prev, [name]: value }));
    if (patientMode === "new" && (name === "fullName" || name === "mobileNo")) {
      // stay in new
    } else if (patientMode === "new") {
      // extras ok
    } else {
      setPatientMode("entry");
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    if (patientMode === "existing") return;
    setformData((prev) => ({ ...prev, mobileNo: value || "" }));
    if (patientMode !== "new") setPatientMode("entry");
  };

  const showPatientError = error && !formData.patientId && patientMode !== "new";
  const showNewPatientError =
    error && patientMode === "new" && (!nameTrim || !formData.gender || digits.length < 5);

  const canRegisterPrimary =
    searchSettled &&
    !searchLoading &&
    matches.length === 0 &&
    queryReady &&
    patientMode !== "new" &&
    patientMode !== "existing";

  const canRegisterQuiet =
    searchSettled &&
    !searchLoading &&
    matches.length > 0 &&
    queryReady &&
    patientMode !== "new" &&
    patientMode !== "existing";

  const selectedVisitLabel = resolvePatientTypeLabelFromPatientAndTreatments({
    patientTypeLabel: formData.patientTypeLabel,
    registerdDate: formData.registerdDate,
    createdAt: formData.createdAt,
  });

  const phoneNames = useMemo(
    () =>
      phonePatients
        .map((p) => String(p.fullName || "").trim())
        .filter(Boolean),
    [phonePatients]
  );

  if (patientMode === "existing" && formData.patientId) {
    return (
      <div className="space-y-2">
        <div
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
            showPatientError
              ? "border-red-400 bg-red-50/50"
              : "border-primary/25 bg-primary/5"
          }`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-white">
            {formData.fullName?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {formData.fullName}
              </p>
              <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                Exist
              </span>
              <PatientTypeBadges patientTypeLabel={selectedVisitLabel} />
            </div>
            <p className="text-xs text-slate-500 truncate">
              {[formData.mobileNo || "No phone", formData.regNo]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <CheckCircleIcon className="text-primary shrink-0" sx={{ fontSize: 18 }} />
          {onViewPatient ? (
            <button
              type="button"
              onClick={onViewPatient}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RemoveRedEyeIcon sx={{ fontSize: 14 }} />
              View
            </button>
          ) : null}
          <button
            type="button"
            onClick={clearSelection}
            className="shrink-0 text-xs font-semibold text-primary hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={`${UI.label} mb-1.5`}>
          Full name {patientMode === "new" ? <span className="text-red-500">*</span> : null}
        </label>
        <div className="flex gap-2 items-center w-full">
          {patientMode === "new" ? (
            <select
              name="title"
              onChange={handleFieldChange}
              value={formData.title}
              aria-label="Title"
              className={`${UI.inputInline} ${UI.selectTitle}`}
            >
              <option value="">Title</option>
              {patienttitles.map((item: any, i: number) => (
                <option key={i} value={item.value}>
                  {item.title}
                </option>
              ))}
            </select>
          ) : null}
          <input
            ref={fullNameRef}
            type="text"
            name="fullName"
            autoComplete="name"
            value={formData.fullName}
            onChange={handleFieldChange}
            placeholder="Full name"
            className={`${UI.inputInline} ${UI.inputGrow} ${
              showNewPatientError && !nameTrim ? "border-red-400" : ""
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches.length > 0 && patientMode !== "new") {
                e.preventDefault();
                selectExisting(matches[0]);
              }
            }}
          />
        </div>
      </div>

      <div>
        <label className={`${UI.label} mb-1.5`}>
          Mobile {patientMode === "new" ? <span className="text-red-500">*</span> : null}
        </label>
        <div className="[&_.PhoneInput]:bg-white [&_.PhoneInput]:border [&_.PhoneInput]:border-slate-200/90 [&_.PhoneInput]:rounded-xl [&_.PhoneInput]:px-3 [&_.PhoneInput]:py-2 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:text-slate-800">
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry="LK"
            value={formData.mobileNo || undefined}
            onChange={handlePhoneChange}
          />
        </div>
      </div>

      {patientMode !== "new" ? (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Possible matches
            {searchLoading ? " · Searching…" : null}
          </p>
          <div
            className={`rounded-xl border max-h-[160px] overflow-y-auto bg-white ${
              showPatientError ? "border-red-400" : "border-slate-200/90"
            }`}
          >
            {!searchReady ? (
              <div className="px-3 py-4 text-center text-slate-400">
                <PersonIcon sx={{ fontSize: 24 }} className="mb-0.5 opacity-60" />
                <p className="text-xs">Type a name or phone to find a patient</p>
              </div>
            ) : searchLoading || !searchSettled ? (
              <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>
            ) : matches.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500 text-center">
                No matching patients
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {matches.map((p: any) => {
                  const visitLabel =
                    resolvePatientTypeLabelFromPatientAndTreatments(p);
                  return (
                    <li key={p._id}>
                      <button
                        type="button"
                        onClick={() => selectExisting(p)}
                        className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-50"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-slate-900 truncate block">
                            {p?.fullName}
                          </span>
                          <span className="text-xs text-slate-500">
                            {[p?.regNo, p?.mobileNo || "No phone"]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                        <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                          Exist
                        </span>
                        <PatientTypeBadges patientTypeLabel={visitLabel} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {canRegisterQuiet ? (
            <button
              type="button"
              onClick={startRegisterNew}
              className="text-xs text-slate-500 hover:text-primary hover:underline"
            >
              Not in this list? Register as new
            </button>
          ) : null}

          {canRegisterPrimary ? (
            <button
              type="button"
              onClick={startRegisterNew}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 hover:border-primary/40 hover:bg-primary/5"
            >
              Register as new patient
            </button>
          ) : null}
        </div>
      ) : null}

      {patientMode === "new" ? (
        <div className="space-y-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-slate-700">New patient details</p>
            <button
              type="button"
              onClick={() => setPatientMode("entry")}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Back to search
            </button>
          </div>

          {phoneStatus === "exists" && phonePatients.length > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 space-y-2">
              <p className="text-xs text-amber-900 leading-snug">
                This number is already used by{" "}
                <span className="font-semibold">{phoneNames.join(", ")}</span>.
                Is this a different family member?
              </p>
              <ul className="space-y-1">
                {phonePatients.map((p: any) => (
                  <li key={p._id} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-700 truncate">
                      {p.fullName}
                      {p.regNo ? ` · ${p.regNo}` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => selectExisting(p)}
                      className="shrink-0 text-xs font-semibold text-primary hover:underline"
                    >
                      Use {String(p.fullName || "patient").split(/\s+/)[0]}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div>
            <label className={UI.label}>
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              required
              onChange={handleFieldChange}
              value={formData.gender}
              className={`${UI.input} ${showNewPatientError && !formData.gender ? "border-red-400" : ""}`}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className={UI.label}>Date of birth</label>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleFieldChange}
              className={UI.input}
            />
          </div>

          <div>
            <label className={UI.label}>Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleFieldChange}
              placeholder="Enter address"
              className={UI.input}
            />
          </div>
        </div>
      ) : null}

      {showPatientError ? (
        <p className="text-xs text-red-500">Select a patient or register as new</p>
      ) : null}
      {showNewPatientError ? (
        <p className="text-xs text-red-500">
          Name, gender, and mobile are required for a new patient
        </p>
      ) : null}
    </div>
  );
}
